import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";
import lesson0426 from "../lesson-26/lesson";

const linkLesson0426 = getLinkForCourseLesson("e0db4477-1995-4380-9c35-2338dcf5e504", lesson0426.id);

const theory = `<h3 class=sectionHead>Introduction</h3>
<p>
   When reproducing or recording sound, one usually looks for a linear behavior, with the output being linearly proportional to the input. Most transducers have an operating range in which they behave linearly [1, 2, 4]. This range is limited for low input levels by the noise level and for high input levels by a significant loss of fidelity or by transducer deterioration (see <a href=# data-img-alt="Figure 1. Input-output response diagram" data-img-src=${config.ASSETS_URL}/04/08/theory/figure-1.svg>Figure 1</a>). For the user, it is essential to know the linearity limits of a transducer in order to control its use.
   <figure class=figure>
<p><img alt="Figure 1. Input-output response diagram." src=${config.ASSETS_URL}/04/08/theory/figure-1.svg><figcaption class=caption>Figure 1. Input-output response diagram.</figcaption></figure>
<h3 class=sectionHead>Noise</h3>
<p>In many cases, even if there is no input signal, an output signal is still present. This signal is called noise and can come from different sources [3]. Two noise families can be generally distinguished:
<ul class=itemize1>
   <li class=itemize>
      <strong><span class=cmbx-10>intrinsic noise</span></strong> - Among this first family, internal electrical noise plays a major role. It is present in different forms:
      <ul class=itemize2>
         <li class=itemize>thermal noise, which is generated by the random motion of electrical charges due to non-zero temperatures,
         <li class=itemize>shot noise, or Schottky noise [5], which appears when an electron is crossing a voltage barrier,
         <li class=itemize>
            flicker noise is due to defects or fluctuations in electrical components. Its amplitude is proportional to
            <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
               <mn>1</mn>
               <mo class=MathClass-bin>∕</mo>
               <msup>
                  <mrow>
                     <mi>f</mi>
                  </mrow>
                  <mrow>
                     <mi>κ</mi>
                  </mrow>
               </msup>
            </math>,
            <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
               <mi>κ</mi>
            </math>
            being a real number close to
            <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
               <mn>1</mn>
            </math>.
      </ul>
      <p>Thermal and shot noises are white noise while flicker noise is closer to a pink noise,
   <li class=itemize><strong><span class=cmbx-10>extrinsic noise</span></strong> - This noise, as the name suggests, comes from external sources. We can mention the reception of electromagnetic waves, omnipresent nowadays, or the electrical network that can be picked-up through a ground loop. The latter results in the emergence of harmonics of the power line. External noise can also come from unexpected mechanical vibrations that are converted into electricity by the transduction process. When a microphone is used, acoustic noise can come from undesirable sources (transportation, ventilation system, neighbours, etc.).
</ul>
<p>
   A spectrum showing typical noise sources is given in <a data-img-alt="Figure 2. Noise sources and their typical frequency ranges." data-img-src=${config.ASSETS_URL}/04/08/theory/figure-2.svg href=#>Figure 2</a>.
   <figure class=figure>
<p><img alt="Figure 2. Noise sources and their typical frequency ranges." src=${config.ASSETS_URL}/04/08/theory/figure-2.svg><figcaption class=caption>Figure 2. Noise sources and their typical frequency ranges.</figcaption></figure>
<p>It is often useful to compare the signal of interest to the noise. In this case the Signal-to-Noise Ratio (SNR) is widely used. It is defined by the following equation
<table class=equation>
   <tr>
      <td style="font-size: 12px;">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mstyle class=text>
               <mtext>SNR</mtext>
            </mstyle>
            <mo class=MathClass-rel>=</mo>
            <mn>10</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi mathvariant=bold-script>𝒫</mi>
                     </mrow>
                     <mrow>
                        <mi>s</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi mathvariant=bold-script>𝒫</mi>
                     </mrow>
                     <mrow>
                        <mi>n</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mn>20</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>A</mi>
                     </mrow>
                     <mrow>
                        <mi>s</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>A</mi>
                     </mrow>
                     <mrow>
                        <mi>n</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi mathvariant=bold-script>𝒫</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi mathvariant=bold-script>𝒫</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>
   are the respective powers of signal and noise. As power is proportional to the squared amplitudes, the signal
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>A</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>
   and noise
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>A</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>
   amplitudes can also be used to calculate the SNR.
<h3 class=sectionHead>Distortion</h3>
<p>When dealing with high excitation levels, it is important to know if the quality of the transducers response is still guaranteed. Distortion indicators can be used to ensure that the linearity of the transducer under consideration is kept within a certain limit. Indeed, many non-linearities result in the presence in the output signal of frequency components not present in the input signal.
<h4 class=subsectionHead>Total harmonic distortion</h4>
<p>
   One of the most used distortion indicator is the Total Harmonic Distortion (THD). It is measured by supplying the transducer with a pure tone signal of frequency
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   (see <a href=# data-img-alt="Figure 3. Generation of higher harmonics by a non-linear system excited by a pure signal of frequency <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>f</mi></mrow><mrow><mn>1</mn></mrow></msub></math>." data-img-src=${config.ASSETS_URL}/04/08/theory/figure-3.svg>Figure 3</a>). Then, THD can be calculated using
<table class=equation>
   <tr>
      <td style="font-size: 6px;">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mstyle class=text>
               <mtext>THD</mtext>
            </mstyle>
            <mo class=MathClass-rel>=</mo>
            <mn>100</mn>
            <mo class=MathClass-bin>×</mo>
            <mfrac>
               <mrow>
                  <msqrt>
                     <mrow>
                        <msubsup>
                           <mrow>
                              <mi>a</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msubsup>
                        <mo class=MathClass-bin>+</mo>
                        <msubsup>
                           <mrow>
                              <mi>a</mi>
                           </mrow>
                           <mrow>
                              <mn>3</mn>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msubsup>
                        <mo class=MathClass-bin>+</mo>
                        <msubsup>
                           <mrow>
                              <mi>a</mi>
                           </mrow>
                           <mrow>
                              <mn>4</mn>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msubsup>
                        <mo class=MathClass-bin>+</mo>
                        <mo class=MathClass-punc>.</mo>
                        <mo class=MathClass-punc>.</mo>
                        <mo class=MathClass-punc>.</mo>
                        <msubsup>
                           <mrow>
                              <mi>a</mi>
                           </mrow>
                           <mrow>
                              <mi>i</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msubsup>
                        <mo class=MathClass-punc>.</mo>
                        <mo class=MathClass-punc>.</mo>
                        <mo class=MathClass-punc>.</mo>
                        <mo class=MathClass-bin>+</mo>
                        <msubsup>
                           <mrow>
                              <mi>a</mi>
                           </mrow>
                           <mrow>
                              <mi>n</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msubsup>
                     </mrow>
                  </msqrt>
               </mrow>
               <mrow>
                  <msqrt>
                     <mrow>
                        <msubsup>
                           <mrow>
                              <mi>a</mi>
                           </mrow>
                           <mrow>
                              <mn>1</mn>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msubsup>
                        <mo class=MathClass-bin>+</mo>
                        <msubsup>
                           <mrow>
                              <mi>a</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msubsup>
                        <mo class=MathClass-bin>+</mo>
                        <msubsup>
                           <mrow>
                              <mi>a</mi>
                           </mrow>
                           <mrow>
                              <mn>3</mn>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msubsup>
                        <mo class=MathClass-bin>+</mo>
                        <mo class=MathClass-punc>.</mo>
                        <mo class=MathClass-punc>.</mo>
                        <mo class=MathClass-punc>.</mo>
                        <msubsup>
                           <mrow>
                              <mi>a</mi>
                           </mrow>
                           <mrow>
                              <mi>i</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msubsup>
                        <mo class=MathClass-punc>.</mo>
                        <mo class=MathClass-punc>.</mo>
                        <mo class=MathClass-punc>.</mo>
                        <mo class=MathClass-bin>+</mo>
                        <msubsup>
                           <mrow>
                              <mi>a</mi>
                           </mrow>
                           <mrow>
                              <mi>n</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msubsup>
                     </mrow>
                  </msqrt>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mn>100</mn>
            <mo class=MathClass-bin>×</mo>
            <mfrac>
               <mrow>
                  <msqrt>
                     <mrow>
                        <munderover accent=false accentunder=false>
                           <mrow>
                              <mo>∑</mo>
                           </mrow>
                           <mrow>
                              <mi>i</mi>
                              <mo class=MathClass-rel>=</mo>
                              <mn>2</mn>
                           </mrow>
                           <mrow>
                              <mi>n</mi>
                           </mrow>
                        </munderover>
                        <msubsup>
                           <mrow>
                              <mi>a</mi>
                           </mrow>
                           <mrow>
                              <mi>i</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msubsup>
                     </mrow>
                  </msqrt>
               </mrow>
               <mrow>
                  <msqrt>
                     <mrow>
                        <munderover accent=false accentunder=false>
                           <mrow>
                              <mo>∑</mo>
                           </mrow>
                           <mrow>
                              <mi>i</mi>
                              <mo class=MathClass-rel>=</mo>
                              <mn>1</mn>
                           </mrow>
                           <mrow>
                              <mi>n</mi>
                           </mrow>
                        </munderover>
                        <msubsup>
                           <mrow>
                              <mi>a</mi>
                           </mrow>
                           <mrow>
                              <mi>i</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msubsup>
                     </mrow>
                  </msqrt>
               </mrow>
            </mfrac>
            <mstyle class=mbox>
               <mtext> [%]</mtext>
            </mstyle>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>a</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
   </math>
   is the amplitude of the i<sup class=textsuperscript><span class=cmr-9>th</span></sup> harmonic. This definition corresponds to the ratio of the RMS value of the harmonics due to non-linearities to the RMS value of the output signal. Please note that another formulation exists for which the denominator of Eq. 2 is equal to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>a</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>.
   <figure class=figure>
<p><img alt="Figure 3. Generation of higher harmonics by a non-linear system excited by a pure signal of frequency <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>f</mi></mrow><mrow><mn>1</mn></mrow></msub></math>." src=${config.ASSETS_URL}/04/08/theory/figure-3.svg><figcaption class=caption>Figure 3. Generation of higher harmonics by a non-linear system excited by a pure signal of frequency <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>f</mi></mrow><mrow><mn>1</mn></mrow></msub></math>.</figcaption></figure>
<p>
   An example showing the output signal of a system with a THD of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>6</mn>
   </math>
   % and excited by a
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>500</mn>
   </math>
    Hz signal is shown in <a href=# data-img-alt="Figure 4. Time domain representation (upper plot) and spectrum (lower plot) of the input and output signals of a non linear system with a THD of <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>6</mn></math>%." data-img-src=${config.ASSETS_URL}/04/08/theory/figure-4.svg>Figure 4</a>.
   <figure class=figure>
<p><img alt="Figure 4. Time domain representation (upper plot) and spectrum (lower plot) of the input and output signals of a non linear system with a THD of <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>6</mn></math>%." src=${config.ASSETS_URL}/04/08/theory/figure-4.svg><figcaption class=caption>Figure 4. Time domain representation (upper plot) and spectrum (lower plot) of the input and output signals of a non linear system with a THD of <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>6</mn></math>%.</figcaption></figure>
<h4 class=subsectionHead>Intermodulation distortion</h4>
<p>
   Intermodulation distortion is measured when driving a non-linear system with two or more pure tones. The output signal will contain the excitation frequencies and their harmonics but also their linear combinations. An illustration of this phenomenon is shown in <a href=# data-img-alt="Figure 5. Intermodulation distortion for two excitation frequencies." data-img-src=${config.ASSETS_URL}/04/08/theory/figure-5.svg>Figure 5</a> for two excitation frequencies
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>, the output signal also contains
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>m</mi>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mi>n</mi>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>, where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>m</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>n</mi>
   </math>
   are integer values.
   <figure class=figure>
<p><img alt="Figure 5. Intermodulation distortion for two excitation frequencies." src=${config.ASSETS_URL}/04/08/theory/figure-5.svg><figcaption class=caption>Figure 5. Intermodulation distortion for two excitation frequencies.</figcaption></figure><figure class=figure><p><img alt="Figure 6. Time domain representation (upper plot) and spectrum (lower plot) of the input and output signals of a non linear system with harmonic (lower part of the spectrum) and intermodulation distortion (higher part of the spectrum). Here, <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>f</mi></mrow><mrow><mn>1</mn></mrow></msub><mo class=MathClass-rel>=</mo><mn>60</mn></math> Hz and <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>f</mi></mrow><mrow><mn>2</mn></mrow></msub><mo class=MathClass-rel>=</mo><mn>7</mn></math> kHz with a <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>4</mn></math> to <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>1</mn></math> amplitude ratio." src=${config.ASSETS_URL}/04/08/theory/figure-6.svg><figcaption class=caption>Figure 6. Time domain representation (upper plot) and spectrum (lower plot) of the input and output signals of a non linear system with harmonic (lower part of the spectrum) and intermodulation distortion (higher part of the spectrum). Here, <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>f</mi></mrow><mrow><mn>1</mn></mrow></msub><mo class=MathClass-rel>=</mo><mn>60</mn></math> Hz and <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>f</mi></mrow><mrow><mn>2</mn></mrow></msub><mo class=MathClass-rel>=</mo><mn>7</mn></math> kHz with a <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>4</mn></math> to <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>1</mn></math> amplitude ratio.</figcaption></figure>
<h4 class=subsectionHead>Root causes of distortion</h4>
<p>
   The root causes of distortion are incredibly varied. They can be intrinsic to the behaviour of the transducer, e.g. electrostatic conversion includes quadratic terms in the governing equations. Transducer materials may also have non-linear behaviour, e.g., stiffness of a suspension of an electrodynamic loudspeaker which is dependent on displacement (see <a href=# data-img-alt="Figure 7. An example of a non-linear parameter of an electrodynamic loudspeaker: stiffness (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>K</mi></mrow><mrow><mi mathvariant=italic>ms</mi></mrow></msub></math>) of the suspension parts as a function of displacement." data-img-src=${config.ASSETS_URL}/04/08/theory/figure-7.svg>Figure 7</a>), and may also exhibit hysteresis. Clipping due to electronic parts embedded in the transducer may also happen.
   <a href=${linkLesson0426} target="_blank">Lesson 26</a> of this course will be devoted to the non-linearities of speakers.
   <figure class=figure>
<p><img alt="Figure 7. An example of a non-linear parameter of an electrodynamic loudspeaker: stiffness (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>K</mi></mrow><mrow><mi mathvariant=italic>ms</mi></mrow></msub></math>) of the suspension parts as a function of displacement." src=${config.ASSETS_URL}/04/08/theory/figure-7.svg><figcaption class=caption>Figure 7. An example of a non-linear parameter of an electrodynamic loudspeaker: stiffness (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>K</mi></mrow><mrow><mi mathvariant=italic>ms</mi></mrow></msub></math>) of the suspension parts as a function of displacement.</figcaption></figure>
<h3 class=sectionHead>Maximum ratings</h3>
<p>
   We have seen that THD is one of the indicators of the fidelity of the system. It is also useful to know the maximum handling power (or level) to avoid damaging the transducer. Such a value is often specified on the data sheet provided by the manufacturer. For example, for loudspeakers, the maximum electrical power specified is measured by checking that the loudspeaker is capable of sustaining it over a long enough time. Manufacturers can also specify short and long term values.<br class=newline>For loudspeakers, manufacturers also specify the maximum excursion
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>X</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>max</mi>
         </mrow>
      </msub>
   </math>
   which gives the distance the voice coil can travel to maintain linear behavior. For microphones, manufacturers usually specify values that ensure that distortion will remain below a given value, for instance 1%, or that the diaphragm will not come into contact with the back plate of the microphone.
<h3 class=sectionHead>Dynamic range</h3>
<p>
   We have seen that the response of a transducer is limited for low levels by noise and for high levels by the decrease of the response quality. It is then possible to define the dynamic range of the considered system which corresponds to the difference between the maximum level of use and the noise level (see red arrow in <a href=# data-img-alt="Figure 8. Dynamic range of a system." data-img-src=${config.ASSETS_URL}/04/08/theory/figure-8.svg>Figure 8</a>).
   <figure class=figure>
<p><img alt="Figure 8. Dynamic range of a system." src=${config.ASSETS_URL}/04/08/theory/figure-8.svg><figcaption class=caption>Figure 8. Dynamic range of a system.</figcaption></figure>
<p>For the user, it is useful to reduce this dynamic range to ensure optimal quality. For example, for low levels, having as much noise as signal is often not enough. One can then define a margin in relation to the background noise, such as a minimum signal-to-noise ratio. For high levels, a margin can also be chosen for instance to avoid clipping due to the acquisition system. This safety zone is often called headroom. One can then define a usable dynamic range which is calculated by subtracting the margins levels from the dynamic range.
<p>
   Here’s an example for a microphone and its preamplifier having a noise level of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>30</mn>
   </math>
    dB and a maximum level of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>140</mn>
   </math>
    dB. The dynamic range will then be equal to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>140</mn>
      <mo class=MathClass-bin>−</mo>
      <mn>30</mn>
      <mo class=MathClass-rel>=</mo>
      <mn>110</mn>
   </math>
    dB. Now, if the user wants to have a minimum SNR of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>20</mn>
   </math>
    dB and headroom of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>10</mn>
   </math>
    dB, the useful dynamic range will be equal to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>130</mn>
      <mo class=MathClass-bin>−</mo>
      <mn>50</mn>
      <mo class=MathClass-rel>=</mo>
      <mn>80</mn>
   </math>
    dB.
<h3 class=sectionHead>Conclusion</h3>
<p>This Lesson introduced the dynamic limits within which a transducer can be used with confidence. For most professional systems, these limits are indicated in the data sheet. For cheaper products, some data may be missing and therefore must be measured if the transducer is to be used safely.
<h3 class=likesectionHead>Glossary</h3>
<p>
<div class=supertabular>
    <dl>
        <dt>RMS</dt>
        <dd>Root Mean Square</dd>
        <dt>SNR</dt>
        <dd>Signal-to-Noise Ratio</dd>
        <dt>THD</dt>
        <dd>Total Harmonic Distortion</dd>
    </dl>
</div>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt class=thebibliography id=X0-Beranek2012>[1]
   <dd class=thebibliography id=bib-1>
      <p>Leo Beranek and Tim Mellow. <span class=cmti-10>Acoustics: sound fields and transducers</span>. Academic Pres, 2012. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 9780123914866.
   <dt class=thebibliography id=X0-Kleiner2013>[2]
   <dd class=thebibliography id=bib-2>
      <p>Mendel Kleiner. <span class=cmti-10>Electroacoustics</span>. CRC Press, 2013. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 9780429073519.
   <dt class=thebibliography id=X0-placko2013fundamentals>[3]
   <dd class=thebibliography id=bib-3>
      <p>Dominique Placko. <span class=cmti-10>Fundamentals of instrumentation and measurement</span>. John Wiley & Sons, 2013. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 9781905209392.
   <dt class=thebibliography id=X0-Rossi1988>[4]
   <dd class=thebibliography id=bib-4>
      <p>Mario Rossi. <span class=cmti-10>Acoustics and Electroacoustics</span>. Artech House, 1988. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 978-0890062555.
   <dt class=thebibliography id=X0-schottky2018spontaneous>[5]
   <dd class=thebibliography id=bib-5>
      <p>Walter Schottky. “On spontaneous current fluctuations in various electrical conductors”. In: <span class=cmti-10>Journal of Micro/Nanolithography, MEMS, and MOEMS</span> 17.4 (2018), pp. 041001–041001.
</dl>`;

export default theory;
